import type React from "react";
import type {ContractDocument} from "../contractExplorerTypes.ts";
import {documentRows, formatNumber, MISSING, OTHER_AMOUNTS, typeLabel} from "../contractExplorerFormat.ts";
import {KeyValue} from "./KeyValue.tsx";
import {Section} from "./Section.tsx";
import {SourceCite} from "./SourceCite.tsx";

type Props = {documents: ContractDocument[]; byId: Record<string, ContractDocument>};

const TYPE_LABEL: Record<string, string> = {
    paid_to_buyer: 'Paid to the buyer', guarantee: 'Guarantee', limit: 'Limit', penalty: 'Penalty', other: 'Other amount',
};

const amountOf = (row: Record<string, unknown>): string => {
    const currency = typeof row.currency === 'string' ? ` ${row.currency}` : '';
    if (typeof row.amount === 'number') return `${formatNumber(row.amount)}${currency}`;
    if (typeof row.rate_pct === 'number') return `${formatNumber(row.rate_pct)} %`;
    return MISSING;
};

/**
 * Money the documents state outside the price lines: what is paid to the
 * buyer, guarantees, limits and penalties. Every document of the bundle, each
 * amount citing its own; nothing is added up.
 */
export const OtherAmounts: React.FC<Props> = ({documents, byId}) => {
    const amounts = documentRows(documents, OTHER_AMOUNTS);
    if (!amounts.length) return null;
    return (
        <Section title={`Other amounts (${amounts.length})`}
                 sub="Amounts the documents state outside the price lines, as each one states them.">
            <div className="ci-card ci-kv">
                {amounts.map(({row, document}) => (
                    <KeyValue key={String(row.amount_id ?? `${document.document_id}:${String(row.line_no)}`)}
                              label={TYPE_LABEL[String(row.amount_type)] ?? typeLabel(String(row.amount_type ?? ''))}
                              note={typeof row.description === 'string' ? row.description : undefined}>
                        <span className="ci-mono">{amountOf(row)}</span>
                        <SourceCite documentId={document.document_id} documents={byId}/>
                    </KeyValue>
                ))}
            </div>
        </Section>
    );
};
